import React from 'react';
import { Button, Card, Tooltip, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { getRuntimeFeatures } from '../../utils/features-runtime';
import { openUpgradeModal } from './upgradeModalStore';
import { FEATURE_BY_KEY, FEATURE_UPSELL } from '../access-control/featureAccess';

const { Text, Title, Paragraph } = Typography;

// Copy reused wherever a control is shown-but-locked (matches the sidebar).
export const LOCKED_TOOLTIP = 'Not included in your plan — click to learn more';

// Hook: does the current user have this feature?
export function useFeature(featureKey) {
    return getRuntimeFeatures().hasFeature(featureKey);
}

export function supportMailto(featureKey) {
    const f = FEATURE_BY_KEY[featureKey];
    const subject = encodeURIComponent(`Feature request${f ? `: ${f.label}` : ''}`);
    const body = encodeURIComponent(
        `Hi GoHire team,\n\nI'd like to enable ${f ? f.label : 'this feature'} for my account.\n\nThanks!`,
    );
    return `mailto:support@gohire.com?subject=${subject}&body=${body}`;
}

// Shared upsell body used by both the modal and the inline locked page.
export const UpsellContent = ({ featureKey, onContact }) => {
    const f = FEATURE_BY_KEY[featureKey];
    const copy = FEATURE_UPSELL[featureKey] || {};
    return (
        <div style={{ textAlign: 'center', padding: '4px 8px' }}>
            <div style={{
                width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
                display: 'grid', placeItems: 'center', background: 'rgba(250,173,20,.14)',
            }}>
                <LockOutlined style={{ fontSize: 26, color: '#faad14' }} />
            </div>
            <Title level={4} style={{ marginBottom: 8 }}>
                {copy.title || `${f ? f.label : 'This feature'} isn’t in your plan`}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                {copy.blurb || 'This feature isn’t enabled for your account.'}
            </Paragraph>
            <Button type="primary" size="large" href={supportMailto(featureKey)} onClick={onContact}>
                Contact support
            </Button>
        </div>
    );
};

// Inline locked page shown when a gated route is opened directly. The real
// feature Component is NOT mounted, so nothing loads and the URL is truly gated.
export const LockedFeaturePage = ({ featureKey }) => (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh', padding: 24 }}>
        <div style={{ maxWidth: 440 }}>
            <UpsellContent featureKey={featureKey} />
        </div>
    </div>
);

// Wrap a DISABLED antd control so it stays clickable and opens the upgrade
// modal. antd's `disabled` swallows pointer events on the control itself, so the
// click has to be caught by a wrapper element around it — that's why this exists
// rather than just putting onClick on the control.
export const LockedControl = ({ featureKey, tooltip, children, style }) => (
    <Tooltip title={tooltip || LOCKED_TOOLTIP} placement="top">
        <span
            role="button"
            tabIndex={0}
            onClick={() => openUpgradeModal(featureKey)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openUpgradeModal(featureKey); }}
            style={{ cursor: 'pointer', display: 'inline-block', ...style }}
        >
            {children}
        </span>
    </Tooltip>
);

// Card-shaped locked placeholder, for swapping out a dashboard widget/chart.
export const FeatureLockCard = ({ featureKey, height = 220, title, blurb }) => {
    const copy = FEATURE_UPSELL[featureKey] || {};
    return (
        <Card
            bordered
            style={{ height, borderStyle: 'dashed', background: '#fafafa' }}
            bodyStyle={{ height: '100%', display: 'grid', placeItems: 'center', padding: 16 }}
        >
            <div style={{ textAlign: 'center', maxWidth: 360 }}>
                <LockOutlined style={{ fontSize: 22, color: '#faad14', marginBottom: 10 }} />
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {title || copy.title || 'Not included in your plan'}
                </div>
                <Text type="secondary" style={{ fontSize: 13 }}>
                    {blurb || copy.blurb || 'This feature isn’t enabled for your account.'}
                </Text>
                <div style={{ marginTop: 12 }}>
                    <Button size="small" onClick={() => openUpgradeModal(featureKey)}>Learn more</Button>
                </div>
            </div>
        </Card>
    );
};

// Compact horizontal locked banner, for swapping out an inline control strip
// (e.g. the message composer) where a full card would be too tall.
export const FeatureLockBanner = ({ featureKey, style }) => {
    const copy = FEATURE_UPSELL[featureKey] || {};
    return (
        <div
            style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                border: '1px dashed #d9d9d9', borderRadius: 8, background: '#fafafa', ...style,
            }}
        >
            <LockOutlined style={{ fontSize: 20, color: '#faad14', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{copy.title || 'Not included in your plan'}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {copy.blurb || 'This feature isn’t enabled for your account.'}
                </Text>
            </div>
            <Button size="small" onClick={() => openUpgradeModal(featureKey)}>Learn more</Button>
        </div>
    );
};

// HOC: render the locked page instead of the feature when the user lacks it.
export function withFeature(featureKey, Component) {
    const Wrapped = (props) => {
        if (useFeature(featureKey)) return <Component {...props} />;
        return <LockedFeaturePage featureKey={featureKey} />;
    };
    Wrapped.displayName = `withFeature(${featureKey})`;
    return Wrapped;
}
